import { useQuery } from '@tanstack/react-query';
import { DonutRing } from '../ui/charts/DonutRing';
import { apiClient } from '../../lib/api';

interface FraudFlagsResponse {
  flags: Record<string, number>;
  clicksFlagged: number;
  totalClicks: number;
  datacenterPct: number;
}

interface QualityCardProps {
  subId: string | null;
  range: string;
  customFrom?: string;
  customTo?: string;
  clicksTotal?: number;
  clicksQuality?: number;
  clicksBot?: number;
}

export function QualityCard({
  subId,
  range,
  customFrom,
  customTo,
  clicksTotal = 0,
  clicksQuality = 0,
  clicksBot = 0,
}: QualityCardProps): JSX.Element {
  const query = useQuery<FraudFlagsResponse, Error>({
    queryKey: [
      'quality-card',
      subId ?? '',
      range,
      customFrom ?? '',
      customTo ?? '',
    ],
    queryFn: async () => {
      if (!subId) throw new Error('No sub_id');
      const params = new URLSearchParams();
      params.set('sub_id', subId);
      if (range === 'custom' && customFrom && customTo) {
        params.set('from', customFrom);
        params.set('to', customTo);
      } else {
        params.set('range', range);
      }
      return apiClient.get<FraudFlagsResponse>(`/api/v1/stats/fraud-flags?${params.toString()}`);
    },
    enabled: subId != null,
    retry: false,
  });

  // Empty state when no sub_id or no data yet
  if (!subId) {
    return (
      <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded">
        <p className="text-[11px] font-mono text-text-tertiary italic py-1">
          no fraud signals
        </p>
      </div>
    );
  }

  const data = query.data;
  const error = query.error;

  // AE not configured or query error — show brief message, don't crash drawer
  if (error) {
    return (
      <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded">
        <p className="text-[11px] font-mono text-neon-red py-1">
          fraud analysis unavailable
        </p>
      </div>
    );
  }

  if (!data || data.totalClicks === 0) {
    return (
      <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded">
        <p className="text-[11px] font-mono text-text-tertiary italic py-1">
          no fraud signals
        </p>
      </div>
    );
  }

  const botRatePct = clicksTotal > 0 ? (clicksBot / clicksTotal) * 100 : 0;

  // Sort flags desc by count
  const sortedFlags = Object.entries(data.flags)
    .sort((a, b) => b[1] - a[1])
    .map(([flag, count]) => ({ flag, count }));

  return (
    <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded">
      <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
        {/* Left: Donut ring */}
        <div className="flex items-center gap-2">
          <DonutRing
            size={64}
            parts={[
              { value: clicksQuality, className: 'text-neon-green', label: 'Quality' },
              { value: clicksBot, className: 'text-neon-red', label: 'Bots' },
            ]}
          />
        </div>

        {/* Center: Bot% number + Right: Flags */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold font-display numeric">{botRatePct.toFixed(1)}%</span>
            <span className="text-xs text-text-tertiary">Bot Rate</span>
          </div>

          {/* Flags list */}
          {sortedFlags.length > 0 ? (
            <div className="space-y-1">
              {sortedFlags.map(({ flag, count }) => (
                <div key={flag} className="flex items-center justify-between gap-2">
                  <span className={`text-[10px] font-mono uppercase ${count >= 3 ? 'text-neon-red' : 'text-neon-orange'} flex-1`}>
                    {flag}
                  </span>
                  <span className="text-[10px] font-mono text-text-tertiary whitespace-nowrap">
                    x{count}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {/* Datacenter % row */}
          {data.datacenterPct > 0 && (
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-border-subtle/40 mt-1">
              <span className="text-xs font-mono text-text-secondary">Datacenter</span>
              <span
                className={`text-[10px] font-mono uppercase ${
                  data.datacenterPct >= 20
                    ? 'text-neon-red'
                    : data.datacenterPct >= 10
                      ? 'text-neon-yellow'
                      : 'text-text-tertiary'
                }`}
              >
                {data.datacenterPct.toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
